import { ChevronRight } from "lucide-react";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate } from "react-router";
import {
  useConfirmEmailChange,
  useDeleteAccount,
  useLogout,
  useMe,
  useRequestDeletion,
  useRequestEmailChange,
  useSetName,
  useSetSubscription,
} from "@/shared/api/auth";
import { errorMessage } from "@/shared/api/client";
import { useProjects } from "@/shared/api/projects";
import { useLegalLinks } from "@/shared/api/site";
import { useSetUsagePreference } from "@/shared/api/usage";
import { formatDate } from "@/shared/lib/labels";
import { Button } from "@/shared/ui/button";
import { Chip } from "@/shared/ui/chip";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { PageTitle } from "@/shared/ui/page-header";
import { SectionLabel } from "@/shared/ui/section-label";
import { Sheet } from "@/shared/ui/sheet";
import { ErrorNote } from "@/shared/ui/state";

type Dialog = "name" | "email" | "delete" | null;

/** Globális profil: fiók (név, e-mail, törlés), hírlevél, tagságok. A projekt-specifikus
 *  dolgok (pl. a könyvről írt vélemény) a projekt saját profil-oldalán vannak. */
export function ProfilePage() {
  const { data: me } = useMe();
  const logout = useLogout();
  const subscription = useSetSubscription();
  const usage = useSetUsagePreference();
  const privacyHref = useLegalLinks().privacy_url;
  const navigate = useNavigate();
  const { data: projects } = useProjects();
  const [dialog, setDialog] = useState<Dialog>(null);
  if (!me) return null;

  const joinable = (projects ?? []).filter((p) => !p.is_member && p.registration_open);
  const close = () => setDialog(null);

  return (
    <div className="mx-auto flex w-full max-w-screen-md flex-col gap-6 px-5 pb-16 pt-6 md:px-8">
      <div className="flex items-center justify-between">
        <PageTitle>Profil</PageTitle>
        <Link to="/app" className="text-[13px] font-semibold text-gold-text">
          Alkalmazások
        </Link>
      </div>

      <section className="flex flex-col gap-3">
        <SectionLabel>Fiók</SectionLabel>
        <dl className="flex flex-col divide-y divide-line rounded-lg border border-line">
          <Row label="Név">
            <div className="flex items-center justify-end gap-3">
              {me.display_name}
              <button
                type="button"
                onClick={() => setDialog("name")}
                className="text-[13px] font-semibold text-gold-text"
              >
                Átírom
              </button>
            </div>
          </Row>
          <Row label="E-mail">
            <div className="flex items-center justify-end gap-3">
              <span className="break-all">{me.email}</span>
              <button
                type="button"
                onClick={() => setDialog("email")}
                className="shrink-0 text-[13px] font-semibold text-gold-text"
              >
                Módosítom
              </button>
            </div>
          </Row>
          <Row label="Hírlevél">
            <div className="flex items-center justify-end gap-3">
              {me.marketing_consent ? (
                <Chip variant="highlight">Feliratkozva</Chip>
              ) : (
                <Chip variant="neutral">Nincs feliratkozva</Chip>
              )}
              <button
                type="button"
                disabled={subscription.isPending}
                onClick={() => subscription.mutate(!me.marketing_consent)}
                className="text-[13px] font-semibold text-gold-text disabled:opacity-50"
              >
                {me.marketing_consent ? "Leiratkozom" : "Feliratkozom"}
              </button>
            </div>
          </Row>
          <Row label="Használati statisztika">
            <div className="flex items-center justify-end gap-3">
              {me.usage_stats ? (
                <Chip variant="highlight">Bekapcsolva</Chip>
              ) : (
                <Chip variant="neutral">Kikapcsolva</Chip>
              )}
              <button
                type="button"
                disabled={usage.isPending}
                onClick={() => usage.mutate(!me.usage_stats)}
                className="text-[13px] font-semibold text-gold-text disabled:opacity-50"
              >
                {me.usage_stats ? "Kikapcsolom" : "Bekapcsolom"}
              </button>
            </div>
          </Row>
          {me.role === "admin" && (
            <Row label="Szerep">
              <Link to="/admin" className="font-semibold text-gold-text">
                Admin felület
              </Link>
            </Row>
          )}
        </dl>
        {subscription.isError && <ErrorNote>{errorMessage(subscription.error)}</ErrorNote>}
        {usage.isError && <ErrorNote>{errorMessage(usage.error)}</ErrorNote>}
        <p className="text-[12px] leading-[1.4] text-muted">
          A hírlevélről minden levél aljáról is le lehet iratkozni. Az e-mail-cím módosítását
          az új címre küldött kóddal erősítjük meg.
        </p>
        <p className="text-[12px] leading-[1.4] text-muted">
          A használati statisztikából látjuk, mely funkciókat használod és milyen gyakran — ez
          segít eldönteni, mit fejlesszünk tovább. Külső szolgáltatóhoz nem kerül, és nem
          használ sütit. Kikapcsolva a korábban rögzített használati adataid is törlődnek.{" "}
          <a href={privacyHref} className="font-semibold text-gold-text">
            Részletek az adatkezelési tájékoztatóban
          </a>
          .
        </p>
      </section>

      <section className="flex flex-col gap-3">
        <SectionLabel>Alkalmazásaim</SectionLabel>
        {me.projects.length > 0 ? (
          <dl className="flex flex-col divide-y divide-line rounded-lg border border-line">
            {me.projects.map((p) => (
              <Row key={p.slug} label={p.name}>
                <span className="text-[13px] text-muted">
                  csatlakoztál: {formatDate(p.joined_at)}
                </span>
              </Row>
            ))}
          </dl>
        ) : (
          <p className="text-[13px] text-muted">Még egyetlen alkalmazáshoz sem csatlakoztál.</p>
        )}
        {joinable.map((p) => (
          <NavRow key={p.slug} to={`${p.app_path}/csatlakozas`}>
            Csatlakozom: {p.name}
          </NavRow>
        ))}
      </section>

      <Button
        variant="outlineInk"
        block
        disabled={logout.isPending}
        onClick={() =>
          logout.mutate(undefined, {
            onSuccess: () => navigate("/app/belepes", { replace: true }),
          })
        }
      >
        Kilépek
      </Button>

      <section className="flex flex-col gap-2 border-t border-line pt-6">
        <SectionLabel>Fiók törlése</SectionLabel>
        <p className="text-[13px] leading-[1.45] text-muted">
          A fiókod és a hozzá tartozó személyes adatok véglegesen törlődnek. A közösségnek
          szánt beküldéseid (tapasztalatok, ajánlások, javítások) névtelenül
          megmaradnak; a könyvről írt véleményed a keresztneveddel marad meg.
        </p>
        <button
          type="button"
          onClick={() => setDialog("delete")}
          className="self-start text-[13px] font-semibold text-red"
        >
          Törlöm a fiókomat
        </button>
      </section>

      <Sheet
        open={dialog === "name"}
        onOpenChange={(v) => !v && close()}
        title="Név módosítása"
        description="Ezt a nevet látják a többiek, ha megosztasz valamit."
      >
        <NameForm current={me.display_name} onDone={close} />
      </Sheet>

      <Sheet
        open={dialog === "email"}
        onOpenChange={(v) => !v && close()}
        title="E-mail cím módosítása"
        description="Az új címre küldünk egy kódot — a csere csak azzal történik meg."
      >
        <EmailForm onDone={close} />
      </Sheet>

      <Sheet
        open={dialog === "delete"}
        onOpenChange={(v) => !v && close()}
        title="Fiók törlése"
        description="Végleges művelet: a mostani címedre küldött kóddal erősítjük meg."
      >
        {/* Törlés után teljes újratöltés a publikus portálra: a fiók már nem létezik,
            így az SPA-n belüli navigáció csak a belépő oldalra dobna. */}
        <DeleteForm email={me.email} onDeleted={() => window.location.assign("/")} />
      </Sheet>
    </div>
  );
}

function NameForm({ current, onDone }: { current: string; onDone: () => void }) {
  const [name, setName] = useState(current);
  const save = useSetName();

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        save.mutate(name.trim(), { onSuccess: onDone });
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pf-name">Hogyan szólíthatunk?</Label>
        <Input
          id="pf-name"
          autoFocus
          maxLength={80}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      {save.isError && <ErrorNote>{errorMessage(save.error)}</ErrorNote>}
      <Button type="submit" block size="lg" disabled={save.isPending || name.trim().length < 2}>
        {save.isPending ? "Mentés…" : "Mentem"}
      </Button>
    </form>
  );
}

function EmailForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const request = useRequestEmailChange();
  const confirm = useConfirmEmailChange();

  if (!sent) {
    return (
      <form
        className="flex flex-col gap-4"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          request.mutate(email.trim(), { onSuccess: () => setSent(true) });
        }}
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="pf-email">Új e-mail cím</Label>
          <Input
            id="pf-email"
            type="email"
            autoComplete="email"
            inputMode="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="uj@pelda.hu"
          />
        </div>
        {request.isError && <ErrorNote>{errorMessage(request.error)}</ErrorNote>}
        <Button type="submit" block size="lg" disabled={request.isPending || !email.trim()}>
          {request.isPending ? "Küldés…" : "Kódot kérek az új címre"}
        </Button>
      </form>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        confirm.mutate({ email: email.trim(), code }, { onSuccess: onDone });
      }}
    >
      <p className="text-[14px] leading-[1.5] text-ink-soft">
        Elküldtük a kódot ide: <span className="font-semibold text-ink">{email.trim()}</span>.
        A csere csak a kód beírásával történik meg.
      </p>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pf-email-code">Hatjegyű kód</Label>
        <Input
          id="pf-email-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="h-14 text-center text-[24px] font-extrabold tracking-[0.2em]"
          placeholder="······"
        />
      </div>
      {confirm.isError && <ErrorNote>{errorMessage(confirm.error)}</ErrorNote>}
      <Button type="submit" block size="lg" disabled={confirm.isPending || code.length !== 6}>
        {confirm.isPending ? "Ellenőrzés…" : "Megerősítem a cserét"}
      </Button>
      <button
        type="button"
        onClick={() => setSent(false)}
        className="text-[13px] font-semibold text-gold-text"
      >
        Másik címet adok meg
      </button>
    </form>
  );
}

function DeleteForm({ email, onDeleted }: { email: string; onDeleted: () => void }) {
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const request = useRequestDeletion();
  const remove = useDeleteAccount();

  if (!sent) {
    return (
      <div className="flex flex-col gap-4">
        <p className="text-[14px] leading-[1.5] text-ink-soft">
          A törlés végleges: a fiókod, a jelöléseid és a privát jegyzeteid eltűnnek. A
          könyvről írt véleményed a keresztneveddel megmarad, a többi beküldésed névtelenül.
          A megerősítő kódot ide küldjük: <span className="font-semibold text-ink">{email}</span>.
        </p>
        {request.isError && <ErrorNote>{errorMessage(request.error)}</ErrorNote>}
        <Button
          variant="outlineInk"
          block
          size="lg"
          disabled={request.isPending}
          onClick={() => request.mutate(undefined, { onSuccess: () => setSent(true) })}
        >
          {request.isPending ? "Küldés…" : "Kódot kérek a törléshez"}
        </Button>
      </div>
    );
  }

  return (
    <form
      className="flex flex-col gap-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        remove.mutate(code, { onSuccess: onDeleted });
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="pf-del-code">Hatjegyű kód</Label>
        <Input
          id="pf-del-code"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          autoFocus
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
          className="h-14 text-center text-[24px] font-extrabold tracking-[0.2em]"
          placeholder="······"
        />
      </div>
      {remove.isError && <ErrorNote>{errorMessage(remove.error)}</ErrorNote>}
      <Button
        type="submit"
        block
        size="lg"
        className="bg-red text-white hover:bg-red"
        disabled={remove.isPending || code.length !== 6}
      >
        {remove.isPending ? "Törlés…" : "Véglegesen törlöm a fiókomat"}
      </Button>
    </form>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex min-h-12 items-center justify-between gap-4 px-4 py-3">
      <dt className="text-[13px] font-semibold text-muted">{label}</dt>
      <dd className="text-right text-[15px] text-ink">{children}</dd>
    </div>
  );
}

function NavRow({ to, children }: { to: string; children: ReactNode }) {
  return (
    <Link
      to={to}
      className="flex min-h-12 items-center justify-between border-t border-line py-3 text-[15px] font-semibold"
    >
      {children}
      <ChevronRight size={20} className="text-muted" />
    </Link>
  );
}
